/**
 * Ribbons (docs/Decisions.md D42): the member's own rack and progress, the
 * org admin's ribbon designer and rack layout, and the staff awards panel,
 * as tabs. Premium tools show an upgrade prompt to a basic organization; the
 * pre-built platform ribbons work on every plan.
 */
import { useState } from 'react';
import { useAuth } from '../lib/auth';
import { useOrg } from '../lib/org';
import { ui, cn } from '../lib/ui';
import Tabs from '../components/Tabs';
import RackEditor from '../components/ribbons/RackEditor';
import RibbonDesigner from '../components/ribbons/RibbonDesigner';
import AwardsPanel from '../components/ribbons/AwardsPanel';
import { REVIEW_NOTICE } from '../components/ribbons/parts';

export default function Ribbons() {
  const { user } = useAuth();
  const { activeOrg } = useOrg();
  const platformStaff = user?.role === 'admin' || user?.platformRole === 'platform-admin';
  const orgRole = activeOrg?.role || null;
  const isOrgAdmin = orgRole === 'org-admin' || platformStaff;
  const isOrgStaff = isOrgAdmin || orgRole === 'org-moderator';

  const tabs = [
    { id: 'rack', label: 'My rack' },
    ...(activeOrg && isOrgAdmin ? [{ id: 'designer', label: 'Designer' }] : []),
    ...(activeOrg && isOrgStaff ? [{ id: 'awards', label: 'Awards' }] : []),
  ];
  const [tab, setTab] = useState('rack');
  const current = tabs.some(t => t.id === tab) ? tab : 'rack';

  return (
    <div className={ui.page}>
      <h1 className={ui.h1}>Ribbons</h1>
      <p className={cn(ui.muted, 'mb-4')}>
        Ribbons and badges recognise what members do across the platform. They follow you into every
        organization.
      </p>
      <p className={cn(ui.hint, 'mb-4')}>{REVIEW_NOTICE}</p>
      {tabs.length > 1 && (
        <div className="mb-6">
          <Tabs
            tabs={tabs}
            value={current}
            onChange={setTab}
            idPrefix="ribbons"
            aria-label="Ribbon sections"
          />
        </div>
      )}
      <div
        role={tabs.length > 1 ? 'tabpanel' : undefined}
        aria-labelledby={tabs.length > 1 ? `ribbons-${current}` : undefined}
      >
        {current === 'rack' && <RackEditor isOrgAdmin={isOrgAdmin} />}
        {current === 'designer' && <RibbonDesigner isOrgAdmin={isOrgAdmin} />}
        {current === 'awards' && <AwardsPanel isOrgAdmin={isOrgAdmin} />}
      </div>
    </div>
  );
}
